import { ApiMethod } from '@wajeez-web/stores/base';
import {
  Server,
  contentClient,
  publicContentClient,
} from '@wajeez-web/core/api-base';
import { CommonActions, AlertType } from '@wajeez-web/stores/common';
import { PaginatedRequest } from '@wajeez-web/core/api';
import { CancelToken } from 'axios';
import {
  thunkErrorHandler,
  thunkErrorHandlerWithoutSdk,
  thunkResponseChecker,
  thunkResponseCheckerSdk,
} from '@wajeez-web/core/utils';
import {
  ContentEntityBadge,
  GetOnboardingRandomContentEntitiesInput,
  SetContentEntityBadgeInput,
} from './types';

export interface SearchContentEntityPaginatedTypes extends PaginatedRequest {
  ContentEntityTypeNameSearchText: string;
  ContentItemType: string;
}
export interface GetContentEntityCollectionWithMediaPagenatedTypes
  extends PaginatedRequest {
  ContenteEntityId: string;
  ContenteEntityType: string | null | undefined;
}

export interface GetAllContentEntitiesByTypePaginatedTypes
  extends PaginatedRequest {
  ContentEntityTypeName: string;
}

export interface GetContentCollectionTypePaginatedTypes
  extends PaginatedRequest {
  ContenteEntityId: string;
  ContenteEntityType: string | null | undefined;
}

export const getContentDetailsById: ApiMethod<
  string | undefined,
  Server.ContentEntityDetailsOutput
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );

    const response = await client.contentEntities_detailsById_get_V1(
      undefined,
      data
    );

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = { ...error.response }.data;

    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    return rejectWithValue(error);
  }
};

export const getContentDetailsByIdMultilingual: ApiMethod<
  string | undefined,
  Server.ContentEntityMultilingualOutput,
  void
> = async (id, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_ContentEntities_Client(
        undefined,
        contentClient
      );

      const response =
        await client.admin_ContentEntities_multilingualById_get_V1(id);

      if (response.success && response.data) {
        return response.data;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};

export const getContentItemDetailsByIdMultilingual: ApiMethod<
  string | undefined,
  Server.ContentEntityMultilingualOutput,
  void
> = async (id, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_ContentEntities_Client(
        undefined,
        contentClient
      );

      const response =
        await client.admin_ContentEntities_multilingualById_get_V1(id);

      if (response.success && response.data) {
        return response.data;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};

export const searchContentEntityPaginated: ApiMethod<
  SearchContentEntityPaginatedTypes,
  Server.ContentEntityOutput[]
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.admin_ContentEntities_search_get_V1(
      undefined,
      data.ContentEntityTypeNameSearchText,
      data.ContentItemType,
      data.page,
      data.pageSize
    );
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    return rejectWithValue(error);
    throw error;
  }
};

export const searchContentEntityPaginatedForSelector = async (
  data: SearchContentEntityPaginatedTypes
) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.admin_ContentEntities_search_get_V1(
      undefined,
      data.ContentEntityTypeNameSearchText,
      data.ContentItemType,
      data.page,
      data.pageSize
    );
    if (response.success && response.data) {
      return response.data;
    }
    return;
  } catch (error: any) {
    console.log('getAllContentEntityTypes', error);
    // Show alert

    throw error;
  }
};

export const searchTracked: ApiMethod<
  {
    SearchText: string | undefined;
    TypeName: string | null | undefined;
    PageNumber: number;
    PageSize: number;
  },
  {
    [key: string]: {
      data: Server.ContentEntityOutput[];
      meta: {
        hasNextPage: boolean | undefined;
        totalCount: number | undefined;
      };
    };
  }
> = async (data, { dispatch, rejectWithValue }) => {
  const { SearchText, TypeName, PageNumber, PageSize } = data;

  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_searchTracked_get_V1(
      undefined,
      SearchText,
      TypeName,
      null,
      PageNumber,
      PageSize
    );
    if (response.success && response.data) {
      const { hasNextPage, totalCount } = response;

      const typeName = TypeName ? TypeName : 'all';

      return {
        [typeName]: {
          data: response.data,
          meta: { hasNextPage, totalCount },
        },
      };
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    return rejectWithValue(error);
  }
};

export const searchTrackedDropdown: ApiMethod<
  {
    SearchText: string | undefined;
    PageNumber: number;
    PageSize: number;
  },
  {
    data: Server.ContentEntityOutput[];
    meta: {
      hasNextPage: boolean | undefined;
      totalCount: number | undefined;
    };
  }
> = async (data, { dispatch, rejectWithValue }) => {
  const { SearchText, PageNumber, PageSize } = data;

  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_searchTracked_get_V1(
      undefined,
      SearchText,
      null,
      null,
      PageNumber,
      PageSize
    );
    if (response.success && response.data) {
      const { hasNextPage, totalCount } = response;

      return {
        data: response.data,
        meta: { hasNextPage, totalCount },
      };
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    return rejectWithValue(error);
  }
};

export const getTopSearchedPhrases: ApiMethod<
  { NumberOfResults: number; TypeName: string | null },
  Server.TopSearchOutput[]
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_topSearches_get_V1_1(
      undefined,
      data.NumberOfResults,
      data.TypeName
    );
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    return rejectWithValue(error);
  }
};

export const editContentEntity: ApiMethod<
  Server.EditContentEntityByIdInput,
  string
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_put_V1(data);
    if (response.success && response.data) {
      dispatch(
        CommonActions.setAlert({
          type: AlertType.SUCCESS,
          messages: [{ message: 'تمت العملية بنجاح', code: 200, field: '' }],
        })
      );
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const editContentEntityMultilingual: ApiMethod<
  Server.EditContentEntityByIdV2Input,
  string,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_ContentEntities_Client(
        undefined,
        contentClient
      );

      const response = await client.admin_ContentEntities_put_V2(data);

      if (response.success && response.data) {
        return response.data;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};

export const createContentEntity: ApiMethod<
  Server.CreateContentEntityByTypeIdInput,
  string
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_post_V1(data);
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const createContentEntityMultilingual: ApiMethod<
  Server.CreateContentEntityByTypeIdV2Input,
  string,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_ContentEntities_Client(
        undefined,
        contentClient
      );

      const response = await client.admin_ContentEntities_post_V2(data);

      if (response.success && response.data) {
        return response.data;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};

export const deleteContentEntity: ApiMethod<string, void, any> = async (
  id,
  { dispatch, rejectWithValue }
) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.admin_ContentEntities_delete_delete_V1({
      id,
    });
    if (response.success) {
      return;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const getContentEntityById: ApiMethod<
  string,
  Server.ContentEntityOutput
> = async (id, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_byId_get_V1(undefined, id);
    if (response.success && response.data) {
      console.log('getContentEntityById', response.data);
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const addContentEntityByIdForHistory: ApiMethod<
  string,
  Server.ContentEntityOutput
> = async (id, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_byId_get_V1(undefined, id);
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const getAllContentEntitiesByType: ApiMethod<
  GetAllContentEntitiesByTypePaginatedTypes,
  Server.ContentEntityDashboardOutput[]
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_byType_get_V1(
      undefined,
      data.ContentEntityTypeName,
      data.page,
      data.pageSize
    );

    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const createContentEntityByType: ApiMethod<
  Server.CreateContentEntityByTypeNameInput,
  string
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_byType_post_V1(data);

    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    console.log('createContentEntityByType', error);

    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const getContentCollection: ApiMethod<
  GetContentCollectionTypePaginatedTypes & { languageId?: string },
  Server.ContentEntityOutput[]
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_getCollection_get_V1(
      data.languageId,
      data.ContenteEntityId,
      !data.ContenteEntityType || data.ContenteEntityType === 'CONTENTELEMENTS'
        ? undefined
        : data.ContenteEntityType,
      data.page,
      data.pageSize
    );

    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const getContentparents: ApiMethod<
  GetContentCollectionTypePaginatedTypes & { languageId?: string },
  Server.ContentEntityOutput[]
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_getParents_get_V1(
      data.languageId,
      data.ContenteEntityId,
      data.ContenteEntityType,
      data.page,
      data.pageSize
    );

    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const getContentEntitiesAsDataTable: ApiMethod<
  Server.ContentEntityLookUpDatatable,
  Server.TableOfContentEntityLookUpColumns
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_lookups_datatable_post_V1(
      data
    );
    const type = data.filteringOperations?.find(
      (ele) => ele.property == 2
    )?.cretiria;
    if (response.success && response.data) {
      return { ...response.data, type };
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    console.log('getContentEntitiesAsDataTable', error);
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const getContentEntitiesToBeExported: ApiMethod<
  Server.ContentEntityLookUpDatatable,
  Server.TableOfContentEntityLookUpColumns
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_lookups_datatable_post_V1(
      data
    );
    const type = data.filteringOperations?.find(
      (ele) => ele.property == 2
    )?.cretiria;
    if (response.success && response.data) {
      return { ...response.data, type };
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    console.log('getContentEntitiesAsDataTable', error);
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const multiblePublish: ApiMethod<
  Server.SetMultipleContentEntityPublishedStateInput,
  Server.ContentEntityPublishedStateOutput[]
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_multiplePublish_state_put_V1(
      data
    );

    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    console.log('multiblePublish', error);
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const setExpectedPublishDate: ApiMethod<
  Server.SetContentEntityExpectedPublishDateInput,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_ContentEntities_Client(
        undefined,
        contentClient
      );

      const response = await client.contentEntities_publish_expectedDate_put_V1(
        data
      );

      if (response.success) {
        return;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};

export const setPublishDate: ApiMethod<
  Server.SetContentEntityPublishDateInput,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_publish_date_put_V1(data);

    if (response.success) {
      return;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    console.log('setPublishDate', error);
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const setPublish: ApiMethod<
  Server.SetContentEntityPublishedStateInput,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_publish_state_put_V1(data);

    if (response.success) {
      return;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    console.log('setPublish', error);
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const updateContentPlans: ApiMethod<
  Server.UpdateContentEntityPlansInput,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_updatePlan_post_V1(data);

    if (response.success) {
      return;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    console.log('updateContentPlans', error);
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const getContentEntitiesByAuthor: ApiMethod<
  Server.ContentEntityWithAuthorsDatatable,
  Server.ContentEntityWithAuthorsDatatable
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_withAuthor_datatable_post_V1(
      data
    );

    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    console.log('getContentEntitiesByAuthor', error);
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const getContentEntityCollectionWithMedia: ApiMethod<
  GetContentEntityCollectionWithMediaPagenatedTypes,
  Server.ContentEntityDashboardWithMediaOutput[]
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_getCollectionWithMedia_get_V1(
      undefined,
      data.ContenteEntityId,
      data.ContenteEntityType,
      data.page,
      data.pageSize
    );

    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    console.log('getContentEntitiesByAuthor', error);
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

// Collections

export const assignContentEntity: ApiMethod<
  Server.AssignItemToCollectionInput,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Collections_Client(
      undefined,
      contentClient
    );
    const response = await client.collections_assignItem_post_V1(data);

    if (response.success) {
      return;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    console.log('assignContentEntity', error);
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const orderItem: ApiMethod<
  Server.ReOrderCollectionItemInput,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Collections_Client(
      undefined,
      contentClient
    );
    const response = await client.collections_orderItem_post_V1(data);

    if (response.success) {
      return;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    console.log('orderItem', error);
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const unassignContentEntity: ApiMethod<
  Server.UnAssignItemFromCollectionInput,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Collections_Client(
      undefined,
      contentClient
    );
    const response = await client.collections_unAssignItem_post_V1(data);

    if (response.success) {
      return;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    console.log('unassignContentEntity', error);
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

//  mobile

export const getContentEntitiesBySelectors: ApiMethod<
  {
    selectors: Server.GetContentEntitiesBySelectorsAndSortersInput;
    uniqueId: string;
    cancelToken?: CancelToken | undefined;
  },
  {
    [key: string]: {
      data: Server.ContentEntityOutput[];
      meta: {
        hasNextPage: boolean | undefined;
        totalCount: number | undefined;
        currentPage: number | undefined;
      };
    };
  }
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_bySelectors_post_V1(
      data.selectors,
      undefined,
      undefined,
      data.cancelToken
    );
    if (response.success && response.data) {
      const { hasNextPage, totalCount, currentPage } = response;

      return {
        [data.uniqueId]: {
          data: response.data,
          meta: { hasNextPage, totalCount, currentPage },
        },
      };
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    if (error.message !== 'canceled') {
      const errors = error.response && JSON.parse(error.response)?.errors;

      dispatch(
        CommonActions.setAlert({
          show: true,
          type: AlertType.WARNING,
          messages: errors || [{ message: 'Something went wrong' }],
        })
      );

      return rejectWithValue(errors);
    } else {
      return rejectWithValue(error);
    }
  }
};
export const getContentEntityItemsById: ApiMethod<
  string | undefined,
  Server.ItemContentOutput[]
> = async (id, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_itemsById_get_V1(
      undefined,
      id
    );
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    return rejectWithValue(error);
    throw error;
  }
};

export const getNextTitle: ApiMethod<
  Server.GetNextContentEntityInput,
  Server.ContentEntityOutput
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntities_next_post_V1(data);
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    return rejectWithValue(error);
    throw error;
  }
};

export const setAdminOrder: ApiMethod<
  {
    id: string;
    adminOrder: number | undefined;
  },
  void,
  any
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );

    const response = await client.contentEntities_setAdminOrder_put_V1(data);

    if (response.success) {
      return;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        for: 'page',
        type: AlertType.WARNING,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(error);
  }
};

export const getContentEntityTags: ApiMethod<string, string[], void> = async (
  id,
  { dispatch, rejectWithValue }
) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );

    const response = await client.admin_ContentEntities_tags_get_V1(id);

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        for: 'page',
        type: AlertType.WARNING,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(error);
  }
};

export const setContentEntityTags: ApiMethod<
  Server.SetContentEntityTagsInput,
  void,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntities_Client(
      undefined,
      contentClient
    );

    const response = await client.admin_ContentEntities_setTags_put_V1(data);

    if (response.success) {
      return;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        for: 'page',
        type: AlertType.WARNING,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(error);
  }
};

export const getContentEntityBadge: ApiMethod<
  string | undefined,
  ContentEntityBadge[],
  void
> = async (id, { dispatch, rejectWithValue }) => {
  return thunkErrorHandlerWithoutSdk(
    async () => {
      return thunkResponseChecker(async () => {
        return contentClient.get(
          'api/content/v1/admin/ContentEntities/badges',
          {
            params: {
              ContentEntityId: id,
              PageNumber: 1,
              PageSize: 10,
            },
          }
        );
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue
  );
};

export const setContentEntityBadge: ApiMethod<
  SetContentEntityBadgeInput,
  void,
  void
> = async (body, { dispatch, rejectWithValue }) => {
  return thunkErrorHandlerWithoutSdk(
    async () => {
      return thunkResponseChecker(async () => {
        return contentClient.put(
          'api/content/v1/admin/ContentEntities/set-badge',
          body
        );
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue
  );
};

export const getOnboardingRandomContentEntities: ApiMethod<
  GetOnboardingRandomContentEntitiesInput,
  Server.GetUserRandomContentEntitiesBasedOnFollowedCategoriesQueryOutput[],
  void
> = async (body, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(async () => {
        const client = new Server.Server_ContentEntities_Client(
          undefined,
          publicContentClient
        );

        return client.contentEntities_onboardingGetRandomContentEntities_get_V1(
          '1',
          body.followedInterestIds
        );
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue
  );
};

export const getOnboardingContentEntityDetailsById: ApiMethod<
  string | undefined,
  Server.ContentEntityDetailsSlimOutput,
  void
> = async (id, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(async () => {
        const client = new Server.Server_LandingPage_Client(
          undefined,
          publicContentClient
        );

        return client.landingPage_getContentEntityById_get_V1('1', id);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue
  );
};

export const getContentEntitySEOById: ApiMethod<
  string | undefined,
  Server.GetContentEntitySeoMultilingualOutput,
  void
> = async (id, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(async () => {
        const client = new Server.Server_ContentEntitySeo_Client(
          undefined,
          contentClient
        );

        return client.admin_ContentEntitySeo_seoByIdMultilingual_get_V1(id);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue
  );
};

export const upsertContentEntitySEO: ApiMethod<
  Server.UpsertContentEntitySeoCommand,
  void,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(async () => {
        const client = new Server.Server_ContentEntitySeo_Client(
          undefined,
          contentClient
        );

        return client.admin_ContentEntitySeo_upsert_post_V1(data);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue
  );
};

export const changeContentEntityArchiveStatus: ApiMethod<
  Server.ArchiveContentEntityCommand,
  void,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(async () => {
        const client = new Server.Server_ContentEntities_Client(
          undefined,
          contentClient
        );

        return client.contentEntities_archive_put_V1(data);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue
  );
};
